// ABOUTME: Player's score card view for an active game.
// ABOUTME: Loads the player's bingo card via gamePlayerId; recovers from stale IDs for authenticated users.
import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, Link } from 'react-router'
import { useAuth } from '@clerk/react'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import {
  gamePlayerActionsControllerGetScoreCardForGamePlayer,
  gamePlayerActionsControllerMarkGameSquareAsPlayed,
  gamePlayerActionsControllerUpdateScoreCardMarks,
  gameActionsControllerClaimBingo,
  gamePlayerActionsControllerGetMyGamePlayer,
  type ScoreCardDto,
  type ScoreCardGameSquareDto,
} from '../api/generated'
import { GameCard, GameCardBody, FormCardHeader } from '../theme'
import { BingoGrid } from '../components/BingoGrid'
import { useGameRoom } from '../hooks/useGameRoom'
import { useGamePlayer } from '../hooks/useGamePlayer'
import { trackGameFinishedAsPlayer } from '../analytics/events'

export function ScoreCardPage() {
  const { gameId } = useParams<{ gameId: string }>()
  const { gamePlayerId, setGamePlayerId } = useGamePlayer(gameId!)
  const { isSignedIn, getToken } = useAuth()
  const [scoreCard, setScoreCard] = useState<ScoreCardDto | null>(null)
  const [squares, setSquares] = useState<ScoreCardGameSquareDto[]>([])
  const [error, setError] = useState(false)
  const [gameEnded, setGameEnded] = useState<{ winnerGamePlayerId: string; winnerFirstName: string } | null>(null)
  const [removed, setRemoved] = useState(false)
  const [claimResult, setClaimResult] = useState<
    | { outcome: 'invalid_marks'; correctedSquares: ScoreCardGameSquareDto[] }
    | { outcome: 'no_bingo' }
    | null
  >(null)
  const [claiming, setClaiming] = useState(false)
  const gamePlayerIdRef = useRef<string | null>(null)

  useEffect(() => {
    async function load() {
      try {
        let resolvedId = gamePlayerId

        if (!resolvedId) {
          if (!isSignedIn) {
            setError(true)
            return
          }
          const token = await getToken()
          const playerRes = await gamePlayerActionsControllerGetMyGamePlayer(
            gameId!,
            { headers: { Authorization: `Bearer ${token}` } },
          )
          if (playerRes.status !== 200) {
            setError(true)
            return
          }
          resolvedId = playerRes.data.id
          setGamePlayerId(resolvedId)
        }

        const cardRes = await gamePlayerActionsControllerGetScoreCardForGamePlayer(
          gameId!,
          resolvedId,
        )

        if (cardRes.status !== 200) {
          if (isSignedIn) {
            // Score card not found for stored ID — try to recover with a fresh lookup
            const token = await getToken()
            const playerRes = await gamePlayerActionsControllerGetMyGamePlayer(
              gameId!,
              { headers: { Authorization: `Bearer ${token}` } },
            )
            if (playerRes.status === 200 && playerRes.data.id !== resolvedId) {
              setGamePlayerId(playerRes.data.id)
              return // effect re-runs with corrected gamePlayerId
            }
          }
          throw new Error('Score card not found')
        }

        setScoreCard(cardRes.data)
        setSquares(cardRes.data.scoreCardGameSquares)
        gamePlayerIdRef.current = cardRes.data.gamePlayerId

        if (cardRes.data.gameStatus === 'ENDED') {
          setGameEnded({
            winnerGamePlayerId: cardRes.data.hasWon ? cardRes.data.gamePlayerId : '',
            winnerFirstName: '',
          })
        }
      } catch {
        setError(true)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, gamePlayerId, isSignedIn])

  const handleEvent = useCallback((event: string, data: unknown) => {
    if (event === 'square.marked') {
      const { gamePlayerId: eventGamePlayerId, scoreCardGameSquareId } = data as {
        gamePlayerId: string
        scoreCardGameSquareId: string
      }
      if (eventGamePlayerId !== gamePlayerIdRef.current) return
      setSquares((prev) =>
        prev.map((sq) =>
          sq.id === scoreCardGameSquareId ? { ...sq, marked: true } : sq,
        ),
      )
    }
    if (event === 'game.ended') {
      const { winnerGamePlayerId, winnerFirstName } = data as { winnerGamePlayerId: string; winnerFirstName: string }
      setGameEnded({ winnerGamePlayerId, winnerFirstName })
      trackGameFinishedAsPlayer(gameId!)
    }
    if (event === 'player.removed') {
      const { gamePlayerId: removedId } = data as { gamePlayerId: string }
      if (removedId === gamePlayerIdRef.current) setRemoved(true)
    }
  }, [gameId])

  const handleReconnect = useCallback(async () => {
    if (!scoreCard) return
    try {
      const cardRes = await gamePlayerActionsControllerGetScoreCardForGamePlayer(
        gameId!,
        scoreCard.gamePlayerId,
      )
      if (cardRes.status === 200) {
        setSquares(cardRes.data.scoreCardGameSquares)
        if (cardRes.data.hasWon)
          setGameEnded({ winnerGamePlayerId: scoreCard.gamePlayerId, winnerFirstName: '' })
      }
    } catch {
      // Network errors on reconnect: state will catch up on the next reconnect.
    }
  }, [gameId, scoreCard])

  useGameRoom({ gameId: gameId!, onEvent: handleEvent, onReconnect: handleReconnect })

  async function handleMark(squareId: string) {
    if (!scoreCard) return
    const sq = squares.find((s) => s.id === squareId)
    if (!sq) return

    if (scoreCard.gameAutomatic) {
      try {
        await gamePlayerActionsControllerMarkGameSquareAsPlayed(
          scoreCard.gameId,
          scoreCard.id,
          sq.gameSquareId,
        )
        setSquares((prev) => prev.map((s) => s.id === squareId ? { ...s, marked: true } : s))
      } catch {
        // Network errors: mark will be applied via WebSocket event when server processes it.
      }
    } else {
      const previousSquares = squares
      const newMarked = !sq.marked
      const updatedSquares = squares.map((s) =>
        s.id === squareId ? { ...s, marked: newMarked } : s,
      )
      setSquares(updatedSquares)
      const markedIds = updatedSquares
        .filter((s) => s.marked && !s.gameSquare?.free)
        .map((s) => s.gameSquareId)
      try {
        await gamePlayerActionsControllerUpdateScoreCardMarks(
          scoreCard.gameId,
          scoreCard.id,
          { marks: markedIds },
        )
      } catch {
        setSquares(previousSquares)
      }
    }
  }

  async function handleAcceptCorrection() {
    if (!scoreCard || claimResult?.outcome !== 'invalid_marks') return
    const correctedSquares = claimResult.correctedSquares
    const markedIds = correctedSquares
      .filter((s) => s.marked && !s.gameSquare?.free)
      .map((s) => s.gameSquareId)
    try {
      await gamePlayerActionsControllerUpdateScoreCardMarks(scoreCard.gameId, scoreCard.id, { marks: markedIds })
      setSquares(correctedSquares)
      setClaimResult(null)
    } catch {
      // Network error: dialog stays open; user can retry.
    }
  }

  async function handleClaimBingo() {
    if (!scoreCard || claiming) return
    setClaiming(true)
    try {
      const res = await gameActionsControllerClaimBingo(scoreCard.gameId, {
        scoreCardId: scoreCard.id,
      })
      if (res.status !== 200) return
      const { outcome, scoreCard: updatedCard } = res.data as {
        outcome: string
        scoreCard: typeof scoreCard
      }
      if (outcome === 'winner') {
        setGameEnded({ winnerGamePlayerId: scoreCard.gamePlayerId, winnerFirstName: '' })
        setSquares(updatedCard.scoreCardGameSquares)
      } else if (outcome === 'invalid_marks') {
        setClaimResult({ outcome: 'invalid_marks', correctedSquares: updatedCard.scoreCardGameSquares })
      } else {
        setClaimResult({ outcome: 'no_bingo' })
      }
    } catch {
      // Network error: bingo claim failed; user can try again.
    } finally {
      setClaiming(false)
    }
  }

  if (error)
    return (
      <Stack spacing={3} pt={4} alignItems="center">
        <GameCard>
          <GameCardBody>
            <Typography>Your score card isn't ready yet. Please try again later.</Typography>
          </GameCardBody>
        </GameCard>
      </Stack>
    )

  if (!scoreCard) return null

  if (removed)
    return (
      <Stack spacing={3} pt={4} alignItems="center">
        <GameCard>
          <GameCardBody>
            <Alert severity="info">You have been removed from this game by the host.</Alert>
          </GameCardBody>
        </GameCard>
      </Stack>
    )

  return (
    <Stack spacing={3} pt={4} alignItems="center">
      <GameCard>
        <FormCardHeader>
          <Typography variant="h4" component="h1">
            {scoreCard.gameTitle}
          </Typography>
        </FormCardHeader>
        <GameCardBody>
          <BingoGrid
            squares={squares}
            onMark={handleMark}
            automatic={scoreCard.gameAutomatic}
          />
        </GameCardBody>
      </GameCard>

      {!scoreCard.gameAutomatic && (
        <Button variant="contained" color="primary" onClick={handleClaimBingo} disabled={claiming}>
          Call Bingo
        </Button>
      )}

      <Dialog
        open={gameEnded !== null}
        aria-labelledby="game-ended-title"
        disableEscapeKeyDown
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="game-ended-title">Game Over</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              {gameEnded?.winnerGamePlayerId === gamePlayerIdRef.current
                ? 'You won!'
                : gameEnded?.winnerFirstName
                  ? `${gameEnded.winnerFirstName} won!`
                  : 'Game over!'}
            </Typography>
            {scoreCard && (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Want to keep the fun going?
                </Typography>
                <Button variant="contained" component={Link} to="/games/new">
                  Host Your Own Game
                </Button>
                {scoreCard.squarePackShared && (
                  <Button
                    variant="outlined"
                    component={Link}
                    to={`/games/new?squarePackId=${scoreCard.squarePackId}`}
                  >
                    Use This Pack
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={claimResult?.outcome === 'invalid_marks'} onClose={() => setClaimResult(null)}>
        <DialogTitle>Uncalled Squares Marked</DialogTitle>
        <DialogContent>
          <Typography>
            Some of your marked squares haven't been called yet. Remove them to check for bingo?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClaimResult(null)}>Keep Marks</Button>
          <Button variant="contained" onClick={handleAcceptCorrection}>Remove & Check</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={claimResult?.outcome === 'no_bingo'} onClose={() => setClaimResult(null)}>
        <DialogTitle>Not Yet!</DialogTitle>
        <DialogContent>
          <Typography>
            Your marks are valid, but you don't have bingo yet. Keep playing!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClaimResult(null)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
